import React from 'react';
import { Tag as TagIcon } from 'lucide-react';
import { isHexColor, readableTextColor } from '@/shared/lib/tagColors';
import { cn } from '@/shared/utils/cn';

interface TagChipProps {
  name: string;
  /** The tag's colour (SC-376). Without one the chip renders in the neutral style. */
  color?: string | null;
  /**
   * A tag that has been removed from the vocabulary but is still on the item (SC-376 decision
   * 1): shown muted and read-only, and never in the tag's colour.
   */
  legacy?: boolean;
  /** Hover text; defaults to the legacy explanation for a legacy chip. */
  title?: string;
  showIcon?: boolean;
  className?: string;
}

export const LEGACY_TAG_TITLE = 'Removed tag - kept on this item for history, but it can no longer be assigned';

/** A feed-item tag rendered as a chip in the tag's own colour (SC-376). */
export const TagChip: React.FC<TagChipProps> = ({ name, color, legacy = false, title, showIcon = true, className }) => {
  const colored = !legacy && isHexColor(color);

  return (
    <span
      className={cn(
        'chip-tag',
        legacy && 'border-dashed bg-transparent italic text-muted-foreground',
        className
      )}
      style={colored ? { backgroundColor: color!, borderColor: color!, color: readableTextColor(color!) } : undefined}
      title={title ?? (legacy ? LEGACY_TAG_TITLE : undefined)}
      data-legacy={legacy || undefined}
    >
      {showIcon && <TagIcon className="h-3 w-3" aria-hidden />}
      {name}
    </span>
  );
};

/** A small dot in the tag's colour, for pickers and filters that keep their own button style. */
export const TagSwatch: React.FC<{ color?: string | null; className?: string }> = ({ color, className }) =>
  isHexColor(color) ? (
    <span
      aria-hidden
      className={cn('inline-block h-2 w-2 shrink-0 rounded-full', className)}
      style={{ backgroundColor: color }}
    />
  ) : null;
